/**
 * Guided dashboard tour: dims + blurs the page, cuts a rounded spotlight around
 * the current feature and explains it in a small RTL card (a bottom sheet on phones).
 *
 * The tour is split into chapters, one per page (dashboard → bot settings →
 * bot development). Each page renders the same component with its own chapter;
 * a step with `continueTo` hands the tour over to the next page through
 * sessionStorage, and that page picks it up on load.
 *
 * Step shape: { target, title, body, sidebar?, pick?, continueTo?, continueLabel? }.
 * A step without a target is shown as a centered card. Targets are looked up by
 * [data-tour="…"] on every frame, so Livewire morphs, lazy loading and polling
 * never leave the spotlight pointing at a stale node. Missing targets are skipped.
 */

const TARGET_WAIT_MS = 3000;
const SPOTLIGHT_PADDING = 8;
const SPOTLIGHT_RADIUS = 14;
const VIEWPORT_MARGIN = 12;
const OFFER_STABLE_MS = 700;
const HANDOFF_KEY = 'roobito:dashboard-tour';
const HANDOFF_TTL_MS = 2 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/** Resolves on the next frame, or after a short timeout when frames are paused (background tab). */
const nextFrame = () => new Promise((resolve) => {
    const timer = setTimeout(resolve, 50);
    requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
    });
});
const faDigits = (value) => Number(value).toLocaleString('fa-IR');

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isPhoneLayout = () => window.matchMedia('(max-width: 767px)').matches;
const isSidebarVisible = () => window.matchMedia('(min-width: 1024px)').matches;

function isElementVisible(element) {
    const rect = element.getBoundingClientRect();

    if (rect.width < 1 || rect.height < 1) {
        return false;
    }

    return window.getComputedStyle(element).visibility !== 'hidden';
}

function findTargets(name) {
    return [...document.querySelectorAll(`[data-tour="${name}"]`)];
}

/** Visible elements for a step; `pick: 'first'` spotlights only the first match (e.g. one flow node). */
function visibleTargets(step) {
    const visible = findTargets(step.target).filter(isElementVisible);

    return step.pick === 'first' ? visible.slice(0, 1) : visible;
}

/**
 * Steps that apply on this screen. Sidebar steps need the desktop sidebar: without it they are
 * dropped, unless they carry the tour to the next page — then they become a centered card.
 */
function applicableSteps(steps) {
    const sidebarVisible = isSidebarVisible();

    return steps.flatMap((step) => {
        if (! step.sidebar || sidebarVisible) {
            return [step];
        }

        return step.continueTo ? [{ ...step, target: null }] : [];
    });
}

function readHandoff() {
    try {
        const raw = window.sessionStorage.getItem(HANDOFF_KEY);
        window.sessionStorage.removeItem(HANDOFF_KEY);
        const handoff = raw ? JSON.parse(raw) : null;

        return handoff && Date.now() - handoff.at < HANDOFF_TTL_MS ? handoff.chapter : null;
    } catch {
        return null;
    }
}

function writeHandoff(chapter) {
    try {
        window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ chapter, at: Date.now() }));

        return true;
    } catch {
        return false;
    }
}

/**
 * The element's on-screen box, cut down to every scrolling/clipping ancestor, so content
 * wider than its container (a scrollable tab strip, a card inside the flow map) is
 * spotlighted only where it is actually visible.
 */
function clippedRect(element) {
    const rect = element.getBoundingClientRect();
    let left = rect.left;
    let top = rect.top;
    let right = rect.right;
    let bottom = rect.bottom;

    for (let ancestor = element.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
        const style = window.getComputedStyle(ancestor);

        if (style.overflowX === 'visible' && style.overflowY === 'visible') {
            continue;
        }

        const bounds = ancestor.getBoundingClientRect();

        if (style.overflowX !== 'visible') {
            left = Math.max(left, bounds.left);
            right = Math.min(right, bounds.right);
        }

        if (style.overflowY !== 'visible') {
            top = Math.max(top, bounds.top);
            bottom = Math.min(bottom, bounds.bottom);
        }
    }

    return { left, top, right: Math.max(left, right), bottom: Math.max(top, bottom) };
}

function unionRect(elements, { clip = true } = {}) {
    const rects = elements.map((element) => (clip ? clippedRect(element) : element.getBoundingClientRect()));

    const left = Math.min(...rects.map((rect) => rect.left));
    const top = Math.min(...rects.map((rect) => rect.top));
    const right = Math.max(...rects.map((rect) => rect.right));
    const bottom = Math.max(...rects.map((rect) => rect.bottom));

    return { left, top, width: right - left, height: bottom - top };
}

function sameRect(a, b) {
    if (a === null || b === null) {
        return a === b;
    }

    return Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5
        && Math.abs(a.w - b.w) < 0.5 && Math.abs(a.h - b.h) < 0.5;
}

export function dashboardTour({ chapter = 'dashboard', chapters = [], persist = false } = {}) {
    return {
        chapter,
        chapters,
        persist,
        queue: [],
        position: 0,
        progressOffset: 0,
        progressTotal: 0,
        active: false,
        offerVisible: false,
        loading: false,
        animating: false,
        phone: isPhoneLayout(),
        hole: null,
        viewport: { w: window.innerWidth, h: window.innerHeight },
        tooltipStyle: '',
        sheetAtTop: false,

        offerHandled: false,
        navigationToken: 0,
        frameId: null,
        offerTimer: null,
        animationTimer: null,
        returnFocusTo: null,
        offerObserver: null,
        listeners: [],

        init() {
            this.listen(window, 'resize', () => {
                this.phone = isPhoneLayout();
                this.viewport = { w: window.innerWidth, h: window.innerHeight };
            });
            this.listen(document, 'keydown', (event) => this.onKeydown(event));
            this.listen(document, 'livewire:navigating', () => this.teardown({ restoreFocus: false }));

            this.offerObserver = new MutationObserver(() => this.syncOffer());
            this.offerObserver.observe(this.$el, { attributes: true, attributeFilter: ['data-offer'] });

            this.syncOffer();

            if (readHandoff() === this.chapter) {
                this.resumeFromPreviousPage();
            }
        },

        destroy() {
            this.teardown({ restoreFocus: false });
            this.offerObserver?.disconnect();
            clearTimeout(this.offerTimer);
            this.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
            this.listeners = [];
        },

        listen(target, type, handler) {
            target.addEventListener(type, handler);
            this.listeners.push([target, type, handler]);
        },

        get current() {
            return this.queue[this.position] ?? null;
        },

        get progressLabel() {
            return `${faDigits(this.progressOffset + this.position + 1)} از ${faDigits(this.progressTotal)}`;
        },

        get progressSegments() {
            return Array.from({ length: this.progressTotal }, (_, index) => index);
        },

        get progressIndex() {
            return this.progressOffset + this.position;
        },

        get nextLabel() {
            if (this.current?.continueLabel) {
                return this.current.continueLabel;
            }

            if (this.isLast) {
                return 'پایان';
            }

            return this.progressIndex === 0 ? 'شروع' : 'بعدی';
        },

        get isFirst() {
            return this.position === 0;
        },

        get isLast() {
            return this.position === this.queue.length - 1;
        },

        /**
         * Four blurred panels surrounding the spotlight (one full-screen panel when
         * there is no target). Plain boxes avoid clip-path quirks on high-DPI phones.
         */
        get blurPanels() {
            const { w: viewportWidth, h: viewportHeight } = this.viewport;
            const box = (left, top, width, height) => `left: ${left}px; top: ${top}px; width: ${Math.max(0, width)}px; height: ${Math.max(0, height)}px`;

            if (this.hole === null) {
                return [box(0, 0, viewportWidth, viewportHeight), box(0, 0, 0, 0), box(0, 0, 0, 0), box(0, 0, 0, 0)];
            }

            const { x, y, w, h } = this.hole;

            return [
                box(0, 0, viewportWidth, y),
                box(0, y + h, viewportWidth, viewportHeight - y - h),
                box(0, y, x, h),
                box(x + w, y, viewportWidth - x - w, h),
            ];
        },

        get ringStyle() {
            if (this.hole === null) {
                return 'display: none';
            }

            const { x, y, w, h } = this.hole;

            return `transform: translate(${x}px, ${y}px); width: ${w}px; height: ${h}px; border-radius: ${SPOTLIGHT_RADIUS}px;`
                + ' box-shadow: 0 0 0 2px rgb(255 255 255 / 0.85), 0 0 0 7px rgb(255 255 255 / 0.1), 0 0 0 200vmax var(--dashboard-tour-dim)';
        },

        /* ---------- offer ---------- */

        syncOffer() {
            if (this.$el.dataset.offer === '1' && ! this.offerHandled && ! this.active) {
                this.queueOffer();
            }
        },

        /**
         * Waits until no modal (e.g. the setup wizard) has been open for a short while,
         * so the offer never pops up behind or on top of another dialog.
         */
        queueOffer() {
            clearTimeout(this.offerTimer);

            const tick = (stableFor) => {
                if (this.active || this.offerHandled) {
                    return;
                }

                if (document.querySelector('dialog[open]') !== null) {
                    this.offerTimer = setTimeout(() => tick(0), 400);

                    return;
                }

                if (stableFor >= OFFER_STABLE_MS) {
                    this.offerVisible = true;

                    return;
                }

                this.offerTimer = setTimeout(() => tick(stableFor + 350), 350);
            };

            tick(0);
        },

        acceptOffer() {
            this.start();
        },

        declineOffer() {
            this.offerVisible = false;
            this.offerHandled = true;
            this.persistSeen();
        },

        persistSeen() {
            if (! this.persist) {
                return;
            }

            try {
                Promise.resolve(this.$wire?.markTourSeen()).catch(() => {});
            } catch {
                // Saving the flag is best-effort; the tour must never block the dashboard.
            }
        },

        /* ---------- tour lifecycle ---------- */

        start() {
            if (this.active) {
                return;
            }

            clearTimeout(this.offerTimer);
            this.offerVisible = false;
            this.offerHandled = true;
            this.persistSeen();

            const chapterSteps = this.chapters.map((entry) => applicableSteps(entry.steps));
            const chapterIndex = this.chapters.findIndex((entry) => entry.key === this.chapter);
            this.queue = chapterSteps[chapterIndex] ?? [];
            this.progressOffset = chapterSteps.slice(0, Math.max(0, chapterIndex)).reduce((sum, steps) => sum + steps.length, 0);
            this.progressTotal = chapterSteps.reduce((sum, steps) => sum + steps.length, 0);

            if (this.queue.length === 0) {
                return;
            }

            this.returnFocusTo = document.activeElement;
            this.phone = isPhoneLayout();
            this.viewport = { w: window.innerWidth, h: window.innerHeight };
            this.hole = null;
            this.active = true;
            this.position = 0;
            this.startFrameLoop();
            this.goTo(0, 1);
        },

        /** Picks up a tour the previous page handed over, once no modal is open. */
        async resumeFromPreviousPage() {
            const deadline = performance.now() + 5000;

            while (document.querySelector('dialog[open]') !== null && performance.now() < deadline) {
                await sleep(200);
            }

            await sleep(250);
            this.start();
        },

        next() {
            if (this.loading) {
                return;
            }

            if (this.current?.continueTo) {
                this.continueOnNextPage(this.current.continueTo);

                return;
            }

            if (this.isLast) {
                this.finish();

                return;
            }

            this.goTo(this.position + 1, 1);
        },

        previous() {
            if (this.loading || this.isFirst) {
                return;
            }

            this.goTo(this.position - 1, -1);
        },

        continueOnNextPage(url) {
            const nextChapter = this.chapters[this.chapters.findIndex((entry) => entry.key === this.chapter) + 1];

            if (! nextChapter || ! writeHandoff(nextChapter.key)) {
                this.finish();

                return;
            }

            this.teardown({ restoreFocus: false });

            if (window.Livewire?.navigate) {
                window.Livewire.navigate(url);
            } else {
                window.location.assign(url);
            }
        },

        skip() {
            this.teardown({ restoreFocus: true });
        },

        finish() {
            this.teardown({ restoreFocus: true });
        },

        teardown({ restoreFocus }) {
            this.navigationToken++;
            this.offerVisible = false;

            if (! this.active) {
                return;
            }

            this.active = false;
            this.loading = false;
            this.hole = null;
            cancelAnimationFrame(this.frameId);
            clearTimeout(this.animationTimer);
            this.frameId = null;

            if (restoreFocus && this.returnFocusTo instanceof HTMLElement && document.contains(this.returnFocusTo)) {
                this.returnFocusTo.focus({ preventScroll: true });
            }

            this.returnFocusTo = null;
        },

        /**
         * Moves to the first usable step from `index` in `direction`, waiting for lazy
         * targets and skipping ones that never appear or are hidden on this screen size.
         */
        async goTo(index, direction) {
            const token = ++this.navigationToken;
            this.loading = true;

            for (let candidate = index; candidate >= 0 && candidate < this.queue.length; candidate += direction) {
                const step = this.queue[candidate];
                const elements = await this.resolveTargets(step, token);

                if (token !== this.navigationToken) {
                    return;
                }

                if (elements === null) {
                    continue;
                }

                if (elements.length > 0) {
                    await this.scrollIntoView(elements, token);

                    if (token !== this.navigationToken) {
                        return;
                    }
                }

                this.showStep(candidate);

                return;
            }

            this.loading = false;

            if (direction > 0) {
                this.finish();
            }
        },

        async resolveTargets(step, token) {
            if (! step.target) {
                return [];
            }

            const deadline = performance.now() + TARGET_WAIT_MS;
            let nudgedPlaceholder = false;

            while (token === this.navigationToken) {
                const elements = findTargets(step.target);

                if (elements.length > 0) {
                    const visible = visibleTargets(step);

                    return visible.length > 0 ? visible : null;
                }

                // Lazy Livewire cards only load once their skeleton scrolls into view,
                // so bring the skeleton on screen to make the real card render.
                const placeholder = document.querySelector(`[data-tour-pending="${step.target}"]`);

                if (placeholder && ! nudgedPlaceholder) {
                    nudgedPlaceholder = true;
                    placeholder.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
                }

                if (performance.now() > deadline) {
                    return null;
                }

                await sleep(100);
            }

            return null;
        },

        async scrollIntoView(elements, token) {
            const rect = unionRect(elements, { clip: false });
            const visibleRect = unionRect(elements);
            const fitsVertically = rect.top >= VIEWPORT_MARGIN && rect.top + rect.height <= window.innerHeight - VIEWPORT_MARGIN;
            // Hidden inside a horizontally scrolled container (e.g. a tab further along the tab bar)?
            // Wide targets (a whole tab strip) are shown where they are, never scrolled sideways.
            const fitsHorizontally = rect.width > window.innerWidth * 0.6
                || (rect.left >= 0 && rect.left + rect.width <= window.innerWidth && visibleRect.width >= rect.width - 1);

            if (fitsVertically && fitsHorizontally) {
                return;
            }

            const block = fitsVertically ? 'nearest' : (rect.height > window.innerHeight * 0.6 ? 'start' : 'center');
            // `inline: 'center'` also scrolls horizontal containers such as the bot settings tab bar.
            // Sideways scrolls inside a container jump instantly: smooth ones can finish after the spotlight is placed.
            const smooth = fitsHorizontally && ! prefersReducedMotion();
            elements[0].scrollIntoView({ block, inline: fitsHorizontally ? 'nearest' : 'center', behavior: smooth ? 'smooth' : 'auto' });

            let lastTop = null;
            let stableFrames = 0;
            const deadline = performance.now() + 900;

            while (token === this.navigationToken && performance.now() < deadline && stableFrames < 4) {
                await nextFrame();
                const { top, left } = elements[0].getBoundingClientRect();
                const position = top + left * 10000;
                stableFrames = lastTop !== null && Math.abs(position - lastTop) < 0.5 ? stableFrames + 1 : 0;
                lastTop = position;
            }
        },

        showStep(index) {
            this.position = index;
            this.loading = false;
            this.animating = ! prefersReducedMotion();
            clearTimeout(this.animationTimer);
            this.animationTimer = setTimeout(() => {
                this.animating = false;
            }, 380);

            this.updateLayout();

            this.$nextTick(() => {
                this.updateLayout();
                this.$refs.nextButton?.focus({ preventScroll: true });
            });
        },

        /* ---------- layout ---------- */

        startFrameLoop() {
            const loop = () => {
                if (! this.active) {
                    return;
                }

                // Keep following the current target even while the next one is loading
                // or being scrolled to, so the spotlight never freezes over the wrong card.
                this.updateLayout();

                this.frameId = requestAnimationFrame(loop);
            };

            cancelAnimationFrame(this.frameId);
            this.frameId = requestAnimationFrame(loop);
        },

        updateLayout() {
            const step = this.current;

            if (! this.active || step === null) {
                return;
            }

            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            if (this.viewport.w !== viewportWidth || this.viewport.h !== viewportHeight) {
                this.viewport = { w: viewportWidth, h: viewportHeight };
            }

            const elements = step.target ? visibleTargets(step) : [];
            let hole = null;

            if (elements.length > 0) {
                const rect = unionRect(elements);
                const left = Math.max(VIEWPORT_MARGIN / 2, rect.left - SPOTLIGHT_PADDING);
                const top = Math.max(VIEWPORT_MARGIN / 2, rect.top - SPOTLIGHT_PADDING);
                const right = Math.min(viewportWidth - VIEWPORT_MARGIN / 2, rect.left + rect.width + SPOTLIGHT_PADDING);
                const bottom = Math.min(viewportHeight - VIEWPORT_MARGIN / 2, rect.top + rect.height + SPOTLIGHT_PADDING);

                if (right > left && bottom > top) {
                    hole = { x: left, y: top, w: right - left, h: bottom - top };
                }
            }

            if (! sameRect(hole, this.hole)) {
                this.hole = hole;
            }

            this.placeTooltip();
        },

        placeTooltip() {
            const tooltip = this.$refs.tooltip;
            const hole = this.hole;
            let style = '';
            let sheetAtTop = false;

            if (this.phone) {
                sheetAtTop = hole !== null && hole.y + hole.h / 2 > this.viewport.h * 0.55;
            } else if (tooltip) {
                const width = Math.min(360, this.viewport.w - VIEWPORT_MARGIN * 2);
                const height = tooltip.offsetHeight || 220;
                const gap = 14;
                let top;
                let left;

                if (hole === null) {
                    top = (this.viewport.h - height) / 2;
                    left = (this.viewport.w - width) / 2;
                } else {
                    const spaceBelow = this.viewport.h - (hole.y + hole.h);
                    const spaceAbove = hole.y;
                    const spaceLeft = hole.x;
                    const spaceRight = this.viewport.w - (hole.x + hole.w);

                    if (spaceBelow >= height + gap + VIEWPORT_MARGIN) {
                        top = hole.y + hole.h + gap;
                        left = hole.x + hole.w / 2 - width / 2;
                    } else if (spaceAbove >= height + gap + VIEWPORT_MARGIN) {
                        top = hole.y - height - gap;
                        left = hole.x + hole.w / 2 - width / 2;
                    } else if (Math.max(spaceLeft, spaceRight) >= width + gap + VIEWPORT_MARGIN) {
                        top = hole.y + hole.h / 2 - height / 2;
                        left = spaceLeft >= spaceRight ? hole.x - width - gap : hole.x + hole.w + gap;
                    } else {
                        top = this.viewport.h - height - VIEWPORT_MARGIN * 2;
                        left = (this.viewport.w - width) / 2;
                    }
                }

                top = Math.min(Math.max(VIEWPORT_MARGIN, top), this.viewport.h - height - VIEWPORT_MARGIN);
                left = Math.min(Math.max(VIEWPORT_MARGIN, left), this.viewport.w - width - VIEWPORT_MARGIN);
                style = `width: ${width}px; transform: translate(${Math.round(left)}px, ${Math.round(top)}px)`;
            }

            if (style !== this.tooltipStyle) {
                this.tooltipStyle = style;
            }

            if (sheetAtTop !== this.sheetAtTop) {
                this.sheetAtTop = sheetAtTop;
            }
        },

        /* ---------- keyboard ---------- */

        onKeydown(event) {
            if (this.active) {
                if (event.key === 'Escape') {
                    event.preventDefault();
                    this.skip();
                } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    this.next();
                } else if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    this.previous();
                } else if (event.key === 'Tab') {
                    this.trapFocus(event);
                }

                return;
            }

            if (this.offerVisible && event.key === 'Escape') {
                this.declineOffer();
            }
        },

        trapFocus(event) {
            const tooltip = this.$refs.tooltip;

            if (! tooltip) {
                return;
            }

            const focusable = [...tooltip.querySelectorAll('button:not([disabled]), a[href]')];

            if (focusable.length === 0) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && (document.activeElement === first || ! tooltip.contains(document.activeElement))) {
                event.preventDefault();
                last.focus();
            } else if (! event.shiftKey && (document.activeElement === last || ! tooltip.contains(document.activeElement))) {
                event.preventDefault();
                first.focus();
            }
        },
    };
}
